// Sentry first, so the SDK is installed before any application module can throw.
// No-op unless REACT_APP_SENTRY_DSN is set at build time.
import Sentry, { sentryEnabled } from "./sentry";

import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

import { Provider } from "react-redux";
import { store } from "./state/store";

import { ConfigProvider, App as AntApp, Result, Button } from "antd";
import themeConfig from "./theme/themeConfig";
import { AuthProvider } from "./context/AuthContext";
import NotifyBridge from "./utils/NotifyBridge";

// Shown instead of a blank white screen when a render throws. The blank screen is
// the specific failure we have been unable to diagnose without error monitoring:
// the user sees nothing, reports "it broke", and there is no trace anywhere.
const Fallback = () => (
  <Result
    status="500"
    title="Something went wrong"
    subTitle="The error has been reported. Reloading usually clears it."
    extra={
      <Button type="primary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    }
  />
);

// When Sentry is disabled the boundary would swallow errors and show the fallback
// with nothing recorded anywhere — strictly worse than letting React log the crash
// to the console. So the boundary is only installed when it can actually report.
const withBoundary = (children) =>
  sentryEnabled ? (
    <Sentry.ErrorBoundary fallback={<Fallback />}>{children}</Sentry.ErrorBoundary>
  ) : (
    children
  );

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ConfigProvider theme={themeConfig}>
        <AntApp>
          <NotifyBridge />
          <AuthProvider>{withBoundary(<App />)}</AuthProvider>
        </AntApp>
      </ConfigProvider>
    </Provider>
  </React.StrictMode>
);

reportWebVitals();
